// src/pages/CorporateBusesPage.jsx
/* global globalThis */

import React, { useEffect, useState, useContext, useCallback } from 'react';
import {
    Typography,
    Box,
    Card,
    CardContent,
    Button,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Snackbar,
    Alert,
    TextField,
    Autocomplete,
    Chip
} from '@mui/material';
import { DirectionsBus, Save, Clear, ArrowBack, Refresh, Schedule as ScheduleIcon } from '@mui/icons-material';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthProvider';
import api from '../utils/axiosConfig';
import PermissionGuard from '../components/PermissionGuard';
import usePermissions from '../hooks/usePermissions';
import ScheduleThresholdsDialog from '../components/ScheduleThresholdsDialog';
import styled from 'styled-components';
import tw from 'twin.macro';

const PageContainer = styled.div`
    ${tw`bg-gray-50 min-h-screen w-full`}
    padding: 2rem;
    max-width: 1200px;
    margin: 0 auto;
`;

const HeaderCard = styled(Card)`
    ${tw`mb-6 shadow-lg`}
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
`;

const CorporateBusesPage = () => {
    const { hasPermission } = usePermissions();
    const { auth } = useContext(AuthContext);
    const { fiscalYear, corporationId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const stateCorporation = location.state?.corporation;
    const stateFiscalYear = location.state?.fiscalYear;

    const [buses, setBuses] = useState([]);
    const [corporationRouteNumbers, setCorporationRouteNumbers] = useState([]);
    const [routeBusAssignments, setRouteBusAssignments] = useState({});
    // Per-route pilot assignments for corporation routes when no bus is selected
    const [routePilotAssignments, setRoutePilotAssignments] = useState({});
    const [availablePilots, setAvailablePilots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [crewChangeIntent, setCrewChangeIntent] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    // Umbrales de horario por ruta: { [routeNumber]: { [scheduleCode]: { routeStartMaxTime, routeEndMarginMinutes } } }
    const [routeThresholds, setRouteThresholds] = useState({});
    const [scheduleModalRoute, setScheduleModalRoute] = useState(null);
    const [savingSchedule, setSavingSchedule] = useState(false);
    // Turnos reales de la corporación (Corporation.schedules[].name) — no hay AM/MD/PM/EX fijo
    // como en colegio, cada corporación define sus propios nombres de turno.
    const [corporationScheduleCodes, setCorporationScheduleCodes] = useState([]);
    const [corporationScheduleNames, setCorporationScheduleNames] = useState({});

    const currentCorporation = stateCorporation;
    const currentFiscalYear = fiscalYear || stateFiscalYear;

    const fetchCorporationData = useCallback(async () => {
        if (!corporationId) return;
        try {
            const resp = await api.get(`/corporations/${corporationId}`, {
                headers: { Authorization: `Bearer ${auth.token}` }
            });
            const corporation = resp.data.corporation;
            if (corporation?.routeNumbers) {
                let routeNumbers = corporation.routeNumbers;

                // Parse if it's a string
                if (typeof routeNumbers === 'string' && routeNumbers.trim()) {
                    try {
                        routeNumbers = JSON.parse(routeNumbers);
                    } catch (err) {
                        console.error('Error parsing routeNumbers:', err);
                        routeNumbers = [];
                    }
                }

                setCorporationRouteNumbers(Array.isArray(routeNumbers) ? routeNumbers : []);
            } else {
                setCorporationRouteNumbers([]);
            }

            // Turnos reales de la corporación (Corporation.schedules: nombres libres definidos por
            // el admin, ej. "Turno 1", "Fin de semana" — nada que ver con AM/MD/PM/EX de colegio).
            let schedules = corporation?.schedules;
            if (typeof schedules === 'string' && schedules.trim()) {
                try {
                    schedules = JSON.parse(schedules);
                } catch (err) {
                    console.error('Error parsing corporation schedules:', err);
                    schedules = [];
                }
            }
            if (!Array.isArray(schedules)) schedules = [];
            const names = schedules.map((s) => String(s?.name || '').trim()).filter(Boolean);
            setCorporationScheduleCodes(names);
            setCorporationScheduleNames(Object.fromEntries(names.map((name) => [name, name])));
        } catch (err) {
            console.error('Error fetching corporation data:', err);
            setSnackbar({ open: true, message: 'Error al obtener datos de la corporación', severity: 'error' });
            setCorporationRouteNumbers([]);
            setCorporationScheduleCodes([]);
            setCorporationScheduleNames({});
        }
    }, [auth.token, corporationId]);

    const fetchBuses = useCallback(async () => {
        if (!corporationId) return;
        try {
            const resp = await api.get('/buses/simple', {
                headers: { Authorization: `Bearer ${auth.token}` }
            });
            const respData = resp.data;
            const allBuses = Array.isArray(respData) ? respData : (Array.isArray(respData?.buses) ? respData.buses : []);
            setBuses(allBuses);

            // Build map of current assignments (routeNumber -> busId)
            const assignments = {};
            const routePilots = {};
            
            allBuses.forEach(bus => {
                if (bus.routeNumber && bus.corporationId === Number.parseInt(corporationId, 10)) {
                    assignments[bus.routeNumber] = bus.id;
                    if (bus.pilotId) {
                        routePilots[bus.routeNumber] = bus.pilotId;
                    }
                }
            });
            setRouteBusAssignments(assignments);
            setRoutePilotAssignments(routePilots);
        } catch (err) {
            console.error('Error fetching buses:', err);
            setSnackbar({ open: true, message: 'Error al obtener buses', severity: 'error' });
            setBuses([]);
        }
    }, [auth.token, corporationId]);

    const fetchPilots = useCallback(async () => {
        if (!corporationId) return;
        try {
            // Fetch only pilots that belong to this corporation
            const url = `/users/pilots?corporationId=${corporationId}`;
            const response = await api.get(url, {
                headers: { Authorization: `Bearer ${auth.token}` }
            });
            const pilots = Array.isArray(response.data.users) ? response.data.users : [];
            setAvailablePilots(pilots);
        } catch (err) {
            console.error('Error fetching pilots:', err);
            setAvailablePilots([]);
        }
    }, [auth.token, corporationId]);

    const fetchRouteAssignments = useCallback(async () => {
        if (!corporationId) return;
        try {
            const response = await api.get(`/route-assignments?corporationId=${corporationId}`, {
                headers: { Authorization: `Bearer ${auth.token}` }
            });
            const assignments = response.data.assignments || response.data || [];
            const routePilots = {};
            const routeThresholdsMap = {};
            assignments.forEach(assignment => {
                if (assignment.routeNumber) {
                    routePilots[assignment.routeNumber] = assignment.pilotId || null;

                    const thresholdsForRoute = {};
                    (assignment.scheduleThresholds || []).forEach((threshold) => {
                        thresholdsForRoute[threshold.scheduleCode] = {
                            routeStartMaxTime: threshold.routeStartMaxTime || '',
                            routeEndMarginMinutes: threshold.routeEndMarginMinutes != null ? String(threshold.routeEndMarginMinutes) : ''
                        };
                    });
                    routeThresholdsMap[assignment.routeNumber] = thresholdsForRoute;
                }
            });
            setRoutePilotAssignments(prev => ({ ...prev, ...routePilots }));
            setRouteThresholds(prev => ({ ...prev, ...routeThresholdsMap }));
        } catch (err) {
            console.error('Error fetching route assignments:', err);
        }
    }, [auth.token, corporationId]);

    useEffect(() => {
        if (auth.token && corporationId) {
            setLoading(true);
            Promise.all([fetchCorporationData(), fetchBuses(), fetchPilots(), fetchRouteAssignments()])
                .finally(() => setLoading(false));
        }
    }, [auth.token, corporationId, fetchCorporationData, fetchBuses, fetchPilots, fetchRouteAssignments]);

    const handleThresholdChange = (routeNumber, scheduleCode, field, value) => {
        setRouteThresholds(prev => ({
            ...prev,
            [routeNumber]: {
                ...prev[routeNumber],
                [scheduleCode]: {
                    ...((prev[routeNumber] || {})[scheduleCode] || {}),
                    [field]: value
                }
            }
        }));
    };

    const buildScheduleThresholdsPayload = (routeNumber) => {
        const routeSchedules = routeThresholds[routeNumber] || {};
        return corporationScheduleCodes
            .map((code) => {
                const entry = routeSchedules[code] || {};
                const hasValue = Boolean(entry.routeStartMaxTime || entry.routeEndMarginMinutes);
                if (!hasValue) return null;

                return {
                    code,
                    routeStartMaxTime: entry.routeStartMaxTime || null,
                    routeEndMarginMinutes: entry.routeEndMarginMinutes !== '' && entry.routeEndMarginMinutes != null ? Number(entry.routeEndMarginMinutes) : null
                };
            })
            .filter(Boolean);
    };

    const handleSaveScheduleModal = async () => {
        if (!scheduleModalRoute) return;
        setSavingSchedule(true);
        try {
            await api.post('/route-assignments/schedules', {
                corporationId: Number.parseInt(corporationId, 10),
                routeNumber: scheduleModalRoute,
                schedules: buildScheduleThresholdsPayload(scheduleModalRoute)
            }, {
                headers: { Authorization: `Bearer ${auth.token}` }
            });

            setSnackbar({ open: true, message: `Horarios de la Ruta ${scheduleModalRoute} guardados`, severity: 'success' });
            setScheduleModalRoute(null);
            await fetchRouteAssignments();
        } catch (err) {
            console.error('Error saving corporation route schedules:', err);
            setSnackbar({ open: true, message: err.response?.data?.message || 'Error al guardar horarios', severity: 'error' });
        } finally {
            setSavingSchedule(false);
        }
    };

    const handleAssignmentChange = (routeNumber, newBusId) => {
        setRouteBusAssignments(prev => ({
            ...prev,
            [routeNumber]: newBusId || null
        }));
    };

    const handleRoutePilotChange = (routeNumber, pilotId) => {
        if (!pilotId) {
            const confirmed = globalThis.confirm(`Confirma desasignar piloto de la Ruta ${routeNumber}.`);
            if (!confirmed) return;
        }

        setRoutePilotAssignments(prev => ({
            ...prev,
            [routeNumber]: pilotId || null
        }));

        setCrewChangeIntent(prev => ({
            ...prev,
            [routeNumber]: {
                ...(prev[routeNumber]),
                pilotTouched: true,
                unassignPilot: !pilotId
            }
        }));
    };

    const handleSaveAssignments = async () => {
        setSaving(true);
        try {
            const payloadAssignments = corporationRouteNumbers.map((routeNumber) => {
                const intent = crewChangeIntent[routeNumber] || {};
                return {
                    routeNumber,
                    busId: routeBusAssignments[routeNumber] || null,
                    pilotId: routePilotAssignments[routeNumber] || null,
                    explicit: {
                        pilotTouched: Boolean(intent.pilotTouched),
                        unassignPilot: Boolean(intent.unassignPilot)
                    }
                };
            });

            const response = await api.post('/route-assignments/commit-batch', {
                corporationId: Number.parseInt(corporationId, 10),
                assignments: payloadAssignments
            }, {
                headers: { Authorization: `Bearer ${auth.token}` }
            });

            setSnackbar({
                open: true,
                message: response?.data?.message || 'Asignaciones guardadas exitosamente',
                severity: 'success'
            });

            setCrewChangeIntent({});
            await Promise.all([fetchBuses(), fetchRouteAssignments()]);
        } catch (err) {
            console.error('Error saving assignments:', err);
            setSnackbar({ open: true, message: `Error al guardar asignaciones: ${err.response?.data?.message || err.message}`, severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleClearAssignments = () => {
        setRouteBusAssignments({});
        setRoutePilotAssignments({});
        setCrewChangeIntent({});
    };

    const handleRefresh = async () => {
        if (!corporationId) return;
        setLoading(true);
        try {
            await Promise.all([fetchCorporationData(), fetchBuses(), fetchPilots(), fetchRouteAssignments()]);
            setCrewChangeIntent({});
            setSnackbar({ open: true, message: 'Datos actualizados', severity: 'success' });
        } catch (err) {
            console.error('Error refreshing data:', err);
            setSnackbar({ open: true, message: 'Error al actualizar datos', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        navigate(-1);
    };

    const getAvailableBuses = (currentRouteNumber) => {
        // Obtener IDs de buses ya asignados a otras rutas de esta corporación en el estado actual
        const busesAssignedToOtherRoutes = new Set();
        Object.entries(routeBusAssignments).forEach(([routeNum, busId]) => {
            if (routeNum !== currentRouteNumber && busId) {
                busesAssignedToOtherRoutes.add(busId);
            }
        });

        return buses.filter(bus => {
            // Si el bus está asignado a la ruta actual, siempre mostrarlo
            if (routeBusAssignments[currentRouteNumber] === bus.id) {
                return true;
            }
            // Excluir buses ya asignados a otras rutas de esta corporación en el estado actual
            if (busesAssignedToOtherRoutes.has(bus.id)) {
                return false;
            }
            // No excluir buses asignados a otros colegios/corporaciones
            // El backend validará y mostrará un error apropiado si el bus ya está asignado
            return true;
        });
    };

    const getAvailablePilotsForRoute = (currentRouteNumber) => {
        const pilotsAssignedToOtherRoutes = new Set();
        Object.entries(routePilotAssignments).forEach(([routeNumber, pilotId]) => {
            if (routeNumber !== String(currentRouteNumber) && pilotId) {
                pilotsAssignedToOtherRoutes.add(Number(pilotId));
            }
        });

        return availablePilots
            .filter((pilot) => {
                if (Number(routePilotAssignments[currentRouteNumber]) === pilot.id) {
                    return true;
                }
                return !pilotsAssignedToOtherRoutes.has(pilot.id);
            })
            .sort((a, b) => {
                const an = (a.name || a.email || '').toLowerCase();
                const bn = (b.name || b.email || '').toLowerCase();
                return an < bn ? -1 : an > bn ? 1 : 0;
            });
    };

    const getBusInfo = (busId) => {
        const bus = buses.find(b => b.id === Number.parseInt(busId, 10));
        if (!bus) return 'Bus no encontrado';
        return `${bus.plate} (Cap: ${bus.capacity || 'N/A'})`;
    };

    return (
        <PageContainer>
            <HeaderCard>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Button
                            startIcon={<ArrowBack />}
                            onClick={handleBack}
                            sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
                        >
                            Volver
                        </Button>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <DirectionsBus sx={{ fontSize: 40 }} />
                        <Box>
                            <Typography variant="h4">Asignación de Buses - {currentCorporation?.name || 'Cargando...'}</Typography>
                            <Typography variant="body2">Año Fiscal {currentFiscalYear}</Typography>
                        </Box>
                    </Box>
                </CardContent>
            </HeaderCard>

            <Card>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">Asignación de Rutas, Buses, Pilotos y Monitoras</Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button 
                                variant="outlined" 
                                startIcon={<Clear />} 
                                onClick={handleClearAssignments}
                                disabled={saving}
                            >
                                Limpiar
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<Refresh />}
                                onClick={handleRefresh}
                                disabled={loading}
                            >
                                Refrescar
                            </Button>
                            <PermissionGuard permission="routes-assign-staff">
                                <Button
                                    variant="contained"
                                    startIcon={<Save />}
                                    onClick={handleSaveAssignments}
                                    disabled={saving}
                                >
                                    {saving ? 'Guardando...' : 'Guardar Asignaciones'}
                                </Button>
                            </PermissionGuard>
                        </Box>
                    </Box>

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : corporationRouteNumbers.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <DirectionsBus sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                            <Typography variant="body1" color="textSecondary">
                                No hay números de ruta configurados para esta corporación.
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                Configure los números de ruta en la gestión de corporaciones.
                            </Typography>
                        </Box>
                    ) : (
                        <TableContainer component={Paper} sx={{ mt: 2, overflowX: 'auto' }}>
                            <Table sx={{ minWidth: 900 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Número de Ruta</strong></TableCell>
                                        <TableCell><strong>Bus Asignado</strong></TableCell>
                                        <TableCell><strong>Piloto</strong></TableCell>
                                        <TableCell><strong>Monitora</strong></TableCell>
                                        <TableCell><strong>Horarios</strong></TableCell>
                                        <TableCell><strong>Estado</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {corporationRouteNumbers.map((routeNumber) => {
                                        const assignedBusId = routeBusAssignments[routeNumber];
                                        const availableBuses = getAvailableBuses(routeNumber);
                                        const availablePilotsForThisRoute = getAvailablePilotsForRoute(routeNumber);
                                        
                                        return (
                                            <TableRow key={routeNumber}>
                                                <TableCell>
                                                    <Typography variant="h6" color="primary">
                                                        Ruta {routeNumber}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Autocomplete
                                                        disableClearable={false}
                                                        options={availableBuses}
                                                        getOptionLabel={(option) => option ? `${option.plate} (${option.capacity || 'N/A'})` : ''}
                                                        isOptionEqualToValue={(option, value) => option && value && option.id === value.id}
                                                        value={availableBuses.find(b => b.id === assignedBusId) || null}
                                                        onChange={(_, newValue) => handleAssignmentChange(routeNumber, newValue ? newValue.id : null)}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label="Seleccionar Bus"
                                                                variant="outlined"
                                                            />
                                                        )}
                                                        renderOption={(props, option) => (
                                                            <li {...props} key={option.id}>
                                                                {getBusInfo(option.id)}
                                                            </li>
                                                        )}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Autocomplete
                                                        disabled={false}
                                                        options={availablePilotsForThisRoute}
                                                        getOptionLabel={(option) => option ? (option.name || option.email) : ''}
                                                        isOptionEqualToValue={(option, value) => option && value && option.id === value.id}
                                                        value={routePilotAssignments[routeNumber] ? availablePilots.find(p => p.id === routePilotAssignments[routeNumber]) || null : null}
                                                        onChange={(_, newValue) => handleRoutePilotChange(routeNumber, newValue ? newValue.id : null)}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label="Seleccionar Piloto"
                                                                variant="outlined"
                                                            />
                                                        )}
                                                        clearOnEscape
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        disabled
                                                        value="No aplica"
                                                        variant="outlined"
                                                        label="Monitora"
                                                        InputProps={{
                                                            readOnly: true,
                                                        }}
                                                        sx={{
                                                            '& .MuiInputBase-input.Mui-disabled': {
                                                                WebkitTextFillColor: '#999',
                                                            }
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<ScheduleIcon />}
                                                        onClick={() => setScheduleModalRoute(routeNumber)}
                                                    >
                                                        Horarios
                                                    </Button>
                                                    {buildScheduleThresholdsPayload(routeNumber).length > 0 && (
                                                        <Chip
                                                            label={`${buildScheduleThresholdsPayload(routeNumber).length} configurado${buildScheduleThresholdsPayload(routeNumber).length > 1 ? 's' : ''}`}
                                                            size="small"
                                                            color="info"
                                                            sx={{ ml: 1 }}
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {(assignedBusId || routePilotAssignments[routeNumber]) ? (
                                                        <Chip 
                                                            label="Asignado" 
                                                            color="success" 
                                                            size="small"
                                                        />
                                                    ) : (
                                                        <Chip 
                                                            label="Sin asignar" 
                                                            color="warning" 
                                                            size="small"
                                                        />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {corporationRouteNumbers.length > 0 && (
                        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Información:
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                • Cada número de ruta puede tener asignado solo un bus
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                • Solo se muestran buses que no están asignados a ningún colegio ni corporación
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                • Los pilotos se pueden asignar también sin un bus; se guardarán como asignaciones de ruta
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                • Solo se muestran pilotos que pertenecen a esta corporación
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                • Las monitoras no aplican para corporaciones
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <ScheduleThresholdsDialog
                open={Boolean(scheduleModalRoute)}
                routeNumber={scheduleModalRoute}
                scheduleCodes={corporationScheduleCodes}
                scheduleNames={corporationScheduleNames}
                scheduleTimes={{}}
                thresholds={routeThresholds[scheduleModalRoute] || {}}
                onThresholdChange={(code, field, value) => handleThresholdChange(scheduleModalRoute, code, field, value)}
                onClose={() => setScheduleModalRoute(null)}
                onSave={hasPermission('routes-assign-staff') ? handleSaveScheduleModal : undefined}
                saving={savingSchedule}
                roleTabsEnabled={false}
            />
        </PageContainer>
    );
};

export default CorporateBusesPage;
